import { FrameworkKey } from '@libs/constants';

export const queryKeys = {
  features: {
    /** Every runtime feature flag, resolved for the current tenant. */
    all: ['features'],
  },
  komplyanceAi: {
    chat: ['komplyanceAi', 'chat'],
    conversations: ['komplyanceAi', 'conversations'],
    conversationById: (conversationId: string) => ['komplyanceAi', 'conversation', conversationId],
    messages: (conversationId: string, firstId?: string, limit?: number) => [
      'komplyanceAi',
      'messages',
      conversationId,
      firstId,
      limit,
    ],
    /**
     * Prefix shared by every page of one conversation's messages.
     *
     * `messages()` folds `firstId`/`limit` into the key, so a conversation can
     * hold several entries at once. Anything that drops "the messages" — a
     * delete, say — means all of them.
     */
    messagesByConversation: (conversationId: string) => [
      'komplyanceAi',
      'messages',
      conversationId,
    ],
    referenceDetail: (dataSetId: string, referenceId: string) => [
      'komplyanceAi',
      'reference',
      dataSetId,
      referenceId,
    ],
  },
  tenant: {
    documents: ['tenant', 'documents'],
    documentsSummary: ['tenant', 'documents', 'summary'],
  },
  assessments: {
    all: (params?: unknown) => ['assessments', 'all', JSON.stringify(params)],
    /**
     * Prefix shared by every paged/filtered variant of the assessments list.
     *
     * `all()` folds its params into the key, so one screen holds several
     * entries at once — the remediation hub alone holds two. Anything that
     * invalidates "the list" means all of them, and was writing the literal
     * `['assessments', 'all']` by hand to say so.
     */
    allPrefix: ['assessments', 'all'],
    stats: ['assessments', 'stats'],
    detail: (assessmentId: string, options?: unknown) => [
      'assessments',
      'detail',
      assessmentId,
      options,
    ],
    results: (assessmentId: string) => ['assessments', 'results', assessmentId],
    /** Live run progress. Polled while a run is in flight, one page at a time. */
    progress: (assessmentId: string, page?: number, limit?: number) => [
      'assessments',
      'progress',
      assessmentId,
      page,
      limit,
    ],
    versionHistory: (assessmentId: string) => ['assessments', 'versionHistory', assessmentId],
    compare: (baselineId: string, comparisonId: string) => [
      'assessments',
      'compare',
      baselineId,
      comparisonId,
    ],
  },
  admin: {
    dashboardSummary: ['admin', 'dashboard', 'summary'],
    inFlight: ['admin', 'dashboard', 'inFlight'],
    incidents: (params?: unknown) => ['admin', 'dashboard', 'incidents', JSON.stringify(params)],
    tenants: (params?: unknown) => ['admin', 'tenants', JSON.stringify(params)],
    // The range MUST be inside the key, or switching windows silently serves
    // the previous window's numbers.
    usageOverview: (params?: unknown) => ['admin', 'usage', 'overview', JSON.stringify(params)],
    usageTenants: (params?: unknown) => ['admin', 'usage', 'tenants', JSON.stringify(params)],
    // Nested under ['admin','tenant',id] so existing broad invalidations reach it.
    tenantUsage: (id: string, params?: unknown) => [
      'admin',
      'tenant',
      id,
      'usage',
      JSON.stringify(params),
    ],
    tenant: (id: string) => ['admin', 'tenant', id],
    tenantMembers: (id: string) => ['admin', 'tenant', id, 'members'],
    tenantAssessments: (id: string, params?: unknown) => [
      'admin',
      'tenant',
      id,
      'assessments',
      JSON.stringify(params),
    ],
    tenantDocuments: (id: string, params?: unknown) => [
      'admin',
      'tenant',
      id,
      'documents',
      JSON.stringify(params),
    ],
    assessmentDocuments: (id: string, assessmentId: string, params?: unknown) => [
      'admin',
      'tenant',
      id,
      'assessments',
      assessmentId,
      'documents',
      JSON.stringify(params),
    ],
    assessmentResults: (id: string, assessmentId: string) => [
      'admin',
      'tenant',
      id,
      'assessments',
      assessmentId,
      'results',
    ],
    profileCategories: (id: string) => ['admin', 'tenant', id, 'profile', 'categories'],
    profileCategory: (id: string, categoryId: string) => [
      'admin',
      'tenant',
      id,
      'profile',
      'categories',
      categoryId,
    ],
    users: (params?: unknown) => ['admin', 'users', JSON.stringify(params)],
    admins: ['admin', 'users', 'admins'],
    demoRequests: (params?: unknown) => ['admin', 'demo-requests', JSON.stringify(params)],
    features: ['admin', 'features'],
  },
  framework: {
    byKey: (frameworkKey: FrameworkKey, version?: string) => [
      'framework',
      'details',
      frameworkKey,
      version,
    ],
  },
  tenantProfile: {
    templateOutline: ['tenantProfile', 'template', 'outline'],
    templateCategory: (categoryId: string) => ['tenantProfile', 'template', 'category', categoryId],
    documents: {
      all: ['tenantProfile', 'documents', 'all'],
      byCategory: (categoryKey: string) => [
        'tenantProfile',
        'documents',
        'byCategory',
        categoryKey,
      ],
      statuses: (ids: string[]) => ['tenantProfile', 'documents', 'statuses', ...ids],
      statusSummary: ['tenantProfile', 'documents', 'statusSummary'],
      paginated: (params: unknown) => [
        'tenantProfile',
        'documents',
        'paginated',
        JSON.stringify(params),
      ],
    },
    versions: ['tenantProfile', 'versions'],
    summary: ['tenantProfile', 'summary'],
    activeTemplate: ['tenantProfile', 'template', 'active'],
    extractionReview: (tenantProfileDocumentId: string) => [
      'tenantProfile',
      'extractionReview',
      tenantProfileDocumentId,
    ],
  },
  remediation: {
    /** Pre-flight scope for one assessment (what would be generated/improved). */
    scope: (assessmentId: string) => ['remediation', 'scope', assessmentId],
    /**
     * Tenant-wide runs. The status selection is part of the key — the dashboard
     * asks for in-flight runs and the remediation list asks for finished ones
     * too, and one cache entry cannot answer both. `runsAll` is the prefix every
     * variant shares, which is what invalidation targets.
     */
    runsAll: ['remediation', 'runs'],
    runs: (statuses?: readonly string[], includeItems = true) => [
      'remediation',
      'runs',
      statuses?.length ? [...statuses].sort().join(',') : 'active',
      // Part of the key, not a detail: the two variants return different shapes
      // (one carries every run's items, the other none), so they cannot share an
      // entry — a widget reading `items` must never be served the slim payload.
      includeItems ? 'withItems' : 'runsOnly',
    ],
    /**
     * The hub's watcher — the slim `runs/status` poll.
     *
     * Deliberately NOT under `runsAll`. The watcher patches that prefix with
     * `setQueriesData` and invalidates it when a run settles; a key beneath it
     * would be patched with a shape it does not have, and invalidated by its own
     * effect. The cost of sitting outside is that `runsListChanged` has to name
     * it explicitly, which is the trade the comment on that helper explains.
     */
    runStatuses: ['remediation', 'runStatuses'],
    /**
     * Prefix shared by every variant of one run — what invalidation targets.
     *
     * Needed because `run()` is now keyed by paging: a mutation that invalidated
     * only `run(runId)` would refresh the unpaged entry the review screen holds
     * and leave whichever page the run screen is showing stale.
     */
    runPrefix: (runId: string) => ['remediation', 'run', runId],
    /**
     * One run's status + item summaries. Polled while the run is non-terminal.
     *
     * The paging is part of the key: the run screen asks for one page of
     * documents while the review screen asks for the run unpaged, and without
     * this each page flip would overwrite the last in the same cache entry.
     */
    run: (runId: string, page?: number, limit?: number) => [
      'remediation',
      'run',
      runId,
      page !== undefined || limit !== undefined ? `${page ?? 1}:${limit ?? ''}` : 'all',
    ],
    /** One run's full review payload (generatedContent + changeSet per item). */
    runItems: (runId: string) => ['remediation', 'run', runId, 'items'],
    /**
     * One figure embedded in a run's content. Keyed by the storage key because
     * that key carries a content hash — the bytes behind it can never change,
     * which is what makes caching it indefinitely safe.
     */
    figure: (runId: string, key: string) => ['remediation', 'run', runId, 'figure', key],
  },
} as const;
